import { useEffect, useState } from "react";
import { TextField, MenuItem, Box, Avatar, Typography } from "@mui/material";
import { map, orderBy } from "lodash";
import { Moment } from "moment";
import { getDocs } from "firebase/firestore";
import { IconBuildingBank } from "@tabler/icons-react";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import Corretora from "../../../../models/corretora";
import CorretoraDb from "../../../../db/corretoraDb";
import { DATA_PONTO_DE_PARTIDA } from "../../../../stores/constantes";
import { felixTokens } from "../../../../theme";

type Props = {
  setPropriedadeNota?: (nome: string, valor: any) => void;
  corretora?: string;
  dataPregao?: Moment;
  numeroNota?: string;
  erros?: { corretora?: boolean; dataPregao?: boolean; numeroNota?: boolean };
  readOnly: boolean;
};

/** Data e nº da nota são dado, não texto: mono tabular dentro do campo. */
const campoDado = {
  "& input": {
    fontFamily: felixTokens.fontMono,
    fontVariantNumeric: "tabular-nums",
  },
};

export default function CabecalhoNota({
  setPropriedadeNota,
  corretora,
  dataPregao,
  numeroNota,
  erros,
  readOnly,
}: Props) {
  const [corretoras, setCorretoras] = useState<Corretora[]>([]);
  useEffect(() => {
    getDocs(new CorretoraDb().collection({ corretoraId: "" })).then(
      (snapshot) =>
        setCorretoras(
          orderBy(
            map(snapshot.docs, (doc) => doc.data()),
            ["avatar", "apelido"]
          )
        )
    );
  }, []);

  return (
    <Box
      display={"flex"}
      flexDirection="column"
      width="100%"
      paddingX={1}
      paddingY={2}
    >
      {/* Section header do sistema: eyebrow mono tracked sobre hairline. */}
      <Box
        borderBottom={1}
        borderColor="divider"
        paddingBottom={1}
        marginBottom={2}
      >
        <Typography variant="eyebrow" color="text.secondary">
          Identificação da nota
        </Typography>
      </Box>
      <Box display={"flex"} flexDirection="row" width="100%">
        <Box>
          <TextField
            id="standard-corretora"
            label="Corretora"
            InputLabelProps={{
              shrink: true,
            }}
            variant="outlined"
            size="small"
            required
            error={erros?.corretora}
            sx={{ width: "36ch" }}
            select
            value={corretora}
            disabled={readOnly}
            onChange={(e) => {
              if (setPropriedadeNota)
                setPropriedadeNota("corretora", e.target.value);
            }}
          >
            {map(corretoras, (corretora) => (
              <MenuItem value={corretora.id} key={corretora.id}>
                <Box display="flex" flexDirection="row" alignItems="center">
                  <Box marginRight={1}>
                    <Avatar
                      src={corretora.avatar}
                      style={{ width: 32, height: 32 }}
                    >
                      <IconBuildingBank />
                    </Avatar>
                  </Box>
                  {corretora.apelido}
                </Box>
              </MenuItem>
            ))}
          </TextField>
        </Box>
        <Box flexGrow={1} />
        <Box>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DatePicker
              sx={{ width: "18ch", ...campoDado }}
              slotProps={{ textField: { size: "small" } }}
              disableFuture
              minDate={
                readOnly
                  ? undefined
                  : DATA_PONTO_DE_PARTIDA.clone().add(1, "month")
              }
              disabled={readOnly}
              value={dataPregao ?? null}
              onChange={(e: any) => {
                if (e?.isValid() && setPropriedadeNota)
                  setPropriedadeNota("dataPregao", e.startOf("day"));
              }}
            />
          </LocalizationProvider>
        </Box>
        <Box paddingX={1} />
        <Box>
          <TextField
            id="standard-numeroNota"
            label="Nº da Nota"
            InputLabelProps={{
              shrink: true,
            }}
            sx={{ width: "14ch", ...campoDado }}
            variant="outlined"
            size="small"
            required
            error={erros?.numeroNota}
            value={numeroNota}
            disabled={readOnly}
            onChange={(e) => {
              if (setPropriedadeNota)
                setPropriedadeNota("numeroNota", e.target.value);
            }}
          />
        </Box>
      </Box>
    </Box>
  );
}
